import { PlusCircleOutlined, SearchOutlined } from "@ant-design/icons";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton } from "@mui/material";
import { Popconfirm, Space, Switch, Typography, message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  SystemConfigsListResponse,
  SystemConfigsResponse,
} from "../../../@type/SystemConfigs.types";
import { SystemConfigsService } from "../../../api/systemConfigsService";
import AddSystemConfigsModal from "../../../components/AddSystemConfigs";
import ActionButton from "../../../components/common/buttons/ActionButton";
import SearchInput from "../../../components/common/fields/SearchInput";
import Card from "../../../components/common/Card";
import MuiTable from "../../../components/common/MuiTable";
import { Column } from "../../../utils/constants";

const SEARCH_INPUT_WIDTH = 300;

const SystemConfigs = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [data, setData] = useState<SystemConfigsResponse[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<number | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<number | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const keywordRef = useRef("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<SystemConfigsResponse | null>(null);

  const getDataTable = useCallback(
    async (keyword?: string) => {
      setLoadingTable(true);
      try {
        const effectiveKeyword = keyword ?? keywordRef.current;
        const response = await SystemConfigsService.list({
          page,
          size,
          sort: "configKey,asc",
          keyword: effectiveKeyword || undefined,
        });
        const payload: SystemConfigsListResponse | undefined = response.data;
        setData(Array.isArray(payload?.data) ? payload.data : []);
        setTotal(payload?.total ?? 0);
      } catch (error) {
        console.log("==== get system configs error: ", error);
        messageApi.error("Failed to load system configs");
      } finally {
        setLoadingTable(false);
      }
    },
    [page, size, messageApi]
  );

  useEffect(() => {
    getDataTable();
  }, [getDataTable]);

  const reloadData = () => {
    getDataTable();
  };

  const handleSearch = () => {
    keywordRef.current = searchKeyword;
    if (page !== 0) {
      // Page reset triggers the fetch via the page effect (single request)
      setPage(0);
    } else {
      getDataTable(searchKeyword);
    }
  };

  const handleClickEdit = async (row: SystemConfigsResponse) => {
    try {
      setLoadingEditId(row.id);
      const response = await SystemConfigsService.getById(row.id);
      if (response.data?.data) {
        setEditData(response.data.data);
        setModalOpen(true);
      }
    } catch (error) {
      messageApi.error("Failed to load configuration details");
    } finally {
      setLoadingEditId(null);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoadingDelete(true);
      await SystemConfigsService.remove(id);
      messageApi.open({ type: "success", content: "Delete item successfully!" });
      getDataTable();
    } catch (error) {
      messageApi.open({ type: "error", content: "Something went wrong!" });
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: number) => {
    try {
      setLoadingStatus(id);
      await SystemConfigsService.updateStatus(id, newStatus);
      messageApi.success("Status updated successfully!");
      getDataTable();
    } catch (error) {
      messageApi.error("Failed to update status");
    } finally {
      setLoadingStatus(null);
    }
  };

  const header: Column[] = [
    {
      id: "id",
      label: "Id",
      minWidth: 60,
      align: "center",
    },
    {
      id: "config_key",
      label: "Config Key",
      minWidth: 180,
      align: "left",
      format: (value: string) => <span className="font-semibold">{value}</span>,
    },
    {
      id: "config_value",
      label: "Config Value",
      minWidth: 220,
      align: "left",
      // Boolean configs are represented by the Status ON/OFF toggle —
      // skip the mono chip so the row never shows two conflicting booleans.
      format: (value: string) =>
        value === "true" || value === "false" ? null : (
          <Typography.Text code ellipsis={{ tooltip: value }} style={{ maxWidth: 260 }}>
            {value}
          </Typography.Text>
        ),
    },
    {
      id: "description",
      label: "Description",
      minWidth: 180,
      align: "left",
      format: (value: string) => value || "-",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (_: number, row: SystemConfigsResponse) => (
        <Switch
          checkedChildren="ON"
          unCheckedChildren="OFF"
          checked={row.status === 1}
          loading={loadingStatus === row.id}
          onChange={(checked) => handleStatusChange(row.id, checked ? 1 : 0)}
        />
      ),
    },
    {
      id: "action",
      label: "Actions",
      minWidth: 140,
      align: "center",
      format: (_: unknown, row: SystemConfigsResponse) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
          <Box
            size={"small"}
            component={IconButton}
            disabled={loadingEditId === row.id || loadingDelete}
            onClick={() => handleClickEdit(row)}
          >
            <EditIcon color="success" />
          </Box>
          <Popconfirm
            title="Are you sure to delete?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(row.id)}
            okButtonProps={{
              style: { background: "#ba2156", border: "none" },
              loading: loadingDelete,
            }}
          >
            <Box size={"small"} component={IconButton} disabled={loadingDelete}>
              <DeleteIcon color={"error"} />
            </Box>
          </Popconfirm>
        </Box>
      ),
    },
  ];

  const openModalAddNew = () => {
    setEditData(null);
    setModalOpen(true);
  };

  return (
    <Card className="user">
      <div className="filter-button mb-3">
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Box width={SEARCH_INPUT_WIDTH}>
              <SearchInput
                placeholder="Search by keyword..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onSearch={handleSearch}
              />
            </Box>
            <ActionButton
              appSize="large"
              startIcon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loadingTable}
            >
              Search
            </ActionButton>
          </Space>
          <Space>
            <ActionButton appSize="large" startIcon={<PlusCircleOutlined />} onClick={openModalAddNew}>
              Add New
            </ActionButton>
          </Space>
        </Space>
      </div>
      <MuiTable
        header={header}
        data={data}
        isLoading={loadingTable}
        page={page}
        rowsPerPage={size}
        totalElements={total}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setSize(Number(event.target.value));
          setPage(0);
        }}
      ></MuiTable>
      {contextHolder}

      {modalOpen && (
        <AddSystemConfigsModal
          open={modalOpen}
          reloadData={reloadData}
          onCancel={() => {
            setModalOpen(false);
            setEditData(null);
          }}
          editData={editData}
        />
      )}
    </Card>
  );
};

export default SystemConfigs;
